// The shell's header, as Main.dc.html (a laptop) and PhoneLibrary.dc.html (a phone) draw it: the
// wordmark, search across everything the member owns, the bell, and the account button. Search
// finds a recording by a topic it's tagged with too ("Delayed completion" under "The Property
// Accelerator, Group 2 recording, 27 Sep", as the product boards' header search does): the Library's
// own results first, then the tags' (lib/tagSearch.js, hub.search_recordings as the member). A hit
// that opens a course recording shows its 16:9 frame in front of its words once that course has Adam's
// own thumbnail on any recording (decision 47, 12 Oct); every other hit looks as before.
// For staff only (stage 5), the account button opens a small menu: Your account, and Admin. A member
// sees the account button as the boards draw it, a link to Account; so does staff viewing as a member,
// since the person then is the member.
import { useEffect, useRef, useState } from 'react'
import { Link } from '../lib/router.jsx'
import { initials as initialsOf } from '../lib/format.js'
import { searchIn } from '../library/search.js'
import { mergeResults, useTagSearch } from '../lib/tagSearch.js'
import { RecordingThumb, useHitThumbs } from '../product/RecordingThumb.jsx'
import { Wordmark, SearchIcon, BellIcon } from './Marks.jsx'

export function useOutside(ref, open, close) {
  useEffect(() => {
    if (!open) return
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) close() }
    const onKey = e => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [ref, open, close])
}

const STAFF_ROLES = ['owner', 'admin', 'support', 'host']

export function Header({ model, person, phone, onOpenBell }) {
  const initials = model?.initials || initialsOf(person?.full_name)
  const staff = STAFF_ROLES.includes(person?.role)
  return phone
    ? <PhoneHeader model={model} initials={initials} onOpenBell={onOpenBell} staff={staff} />
    : <LaptopHeader model={model} initials={initials} onOpenBell={onOpenBell} staff={staff} />
}

// The laptop's account button: a link to Account, or for staff a menu with Admin in it
function AccountMenu({ initials, staff }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useOutside(ref, open, () => setOpen(false))
  if (!staff) return <Link to="/account" className="sh-avatar" aria-label="Your account">{initials}</Link>
  return (
    <span ref={ref} className="sh-account">
      <button type="button" className="sh-avatar" aria-label="Your account and admin" aria-expanded={open} aria-controls="sh-account-menu"
        onClick={() => setOpen(o => !o)}>{initials}</button>
      {open && (
        <div className="sh-pop sh-menu" id="sh-account-menu">
          <Link to="/account" className="sh-menu-item" onClick={() => setOpen(false)}>Your account</Link>
          <Link to="/admin" className="sh-menu-item" onClick={() => setOpen(false)}>Admin</Link>
        </div>
      )}
    </span>
  )
}

function LaptopHeader({ model, initials, onOpenBell, staff }) {
  const [q, setQ] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [bellOpen, setBellOpen] = useState(false)
  const searchRef = useRef(null)
  const bellRef = useRef(null)
  useOutside(searchRef, searchOpen, () => setSearchOpen(false))
  useOutside(bellRef, bellOpen, () => setBellOpen(false))

  const tags = useTagSearch(q, { enabled: Boolean(model) && searchOpen })
  const results = model ? mergeResults(searchIn(model.search, q, 6), tags, 6) : []
  const showResults = searchOpen && q.trim().length > 0
  const hits = useHitThumbs(showResults ? results : [], model?.searchThumbs)

  function toggleBell() {
    const next = !bellOpen
    setBellOpen(next)
    if (next && onOpenBell) onOpenBell()
  }

  return (
    <header className="sh-header">
      <Wordmark className="mark mark--header" />
      <div className="sh-search" ref={searchRef}>
        <span className="sh-search-icon"><SearchIcon /></span>
        <input aria-label="Search everything you own" placeholder="Search everything you own" value={q}
          onChange={e => { setQ(e.target.value); setSearchOpen(true) }} onFocus={() => setSearchOpen(true)} />
        {showResults && (
          <div className="sh-pop sh-results">
            <div className="sh-pop-head">In everything you own</div>
            {hits.rows.map(({ hit: r, card }) => (
              <Link key={r.label + r.where} to={r.href} className={card ? 'sh-row sh-row--thumb' : 'sh-row'} onClick={() => { setSearchOpen(false); setQ('') }}>
                <RecordingThumb card={card} thumbs={hits.thumbs} className="hit-thumb" />
                <span className="sh-row-label">{r.label}</span>
                <span className="sh-row-sub">{r.where}</span>
              </Link>
            ))}
            {results.length === 0 && <div className="sh-none">Nothing matches that in anything you own.</div>}
          </div>
        )}
      </div>
      <div className="sh-actions" ref={bellRef}>
        <button type="button" className="sh-round" aria-label="Notifications" aria-expanded={bellOpen} onClick={toggleBell}>
          <BellIcon />
          {model?.hasUnread && <span className="sh-unread" aria-hidden="true" />}
        </button>
        <AccountMenu initials={initials} staff={staff} />
        {bellOpen && (
          <div className="sh-pop sh-bell">
            <div className="sh-pop-head sh-pop-head--bell">Notifications</div>
            {(model?.notifications || []).map(n => (
              <Link key={n.id} to={n.href} className="sh-row sh-row--note" onClick={() => setBellOpen(false)}>
                <span className="sh-row-label sh-row-label--note">{n.text}</span>
                <span className="sh-row-sub">{n.when}</span>
              </Link>
            ))}
            {model && model.notifications.length === 0 && <div className="sh-none">Nothing new.</div>}
          </div>
        )}
      </div>
    </header>
  )
}

function PhoneHeader({ model, initials, onOpenBell, staff }) {
  const [panel, setPanel] = useState(null)
  const [q, setQ] = useState('')
  const tags = useTagSearch(q, { enabled: Boolean(model) && panel === 'search' })
  const results = model ? mergeResults(searchIn(model.search, q, 4), tags, 4) : []
  const hits = useHitThumbs(panel === 'search' && q.trim() ? results : [], model?.searchThumbs)

  function toggle(which) {
    const next = panel === which ? null : which
    setPanel(next)
    if (next === 'bell' && onOpenBell) onOpenBell()
  }

  return (
    <>
      <header className="ph-header">
        <Wordmark className="mark mark--phone" />
        <span className="ph-actions">
          <button type="button" className="ph-round" aria-label="Search" aria-expanded={panel === 'search'} onClick={() => toggle('search')}>
            <SearchIcon />
          </button>
          <button type="button" className="ph-round" aria-label="Notifications" aria-expanded={panel === 'bell'} onClick={() => toggle('bell')}>
            <BellIcon size={18} />
            {model?.hasUnread && <span className="ph-unread" aria-hidden="true" />}
          </button>
          {staff
            ? <button type="button" className="ph-avatar" aria-label="Your account and admin" aria-expanded={panel === 'account'}
                aria-controls="ph-account-menu" onClick={() => toggle('account')}>{initials}</button>
            : <Link to="/account" className="ph-avatar" aria-label="Your account">{initials}</Link>}
        </span>
      </header>
      {panel === 'account' && (
        <div className="ph-panel ph-panel--account" id="ph-account-menu">
          <Link to="/account" className="ph-row" onClick={() => setPanel(null)}><span className="ph-row-label">Your account</span></Link>
          <Link to="/admin" className="ph-row" onClick={() => setPanel(null)}><span className="ph-row-label">Admin</span></Link>
        </div>
      )}
      {panel === 'search' && (
        <div className="ph-panel ph-panel--search">
          <input aria-label="Search everything you own" placeholder="Search everything you own" value={q}
            onChange={e => setQ(e.target.value)} autoFocus />
          {hits.rows.map(({ hit: r, card }) => (
            <Link key={r.label + r.where} to={r.href} className={card ? 'ph-row ph-row--thumb' : 'ph-row'} onClick={() => setPanel(null)}>
              <RecordingThumb card={card} thumbs={hits.thumbs} className="hit-thumb" />
              <span className="ph-row-label">{r.label}</span>
              <span className="ph-row-sub">{r.where}</span>
            </Link>
          ))}
          {q.trim() && results.length === 0 && <div className="ph-none">Nothing matches that in anything you own.</div>}
        </div>
      )}
      {panel === 'bell' && (
        <div className="ph-panel ph-panel--bell">
          <span className="ph-panel-head">Notifications</span>
          {(model?.notifications || []).map(n => (
            <Link key={n.id} to={n.href} className="ph-row" onClick={() => setPanel(null)}>
              <span className="ph-row-label ph-row-label--note">{n.text}</span>
              <span className="ph-row-sub">{n.when}</span>
            </Link>
          ))}
          {model && model.notifications.length === 0 && <div className="ph-none">Nothing new.</div>}
        </div>
      )}
    </>
  )
}
